import { timeToSlot } from '@/src/hooks/srp/flightTimingFilterManager'
import { FilterData, FlightCard, Sectors } from '@/src/types/flight'

export const filterCards = ({
    selectedLegSortedCardIds: cardIds,
    allCardsForSelectedLeg: cards,
    filterData,
    sectors,
    checkedBaggageIncludedRecommendedFilter,
    selectedJourneyLeg,
    shouldShowMultiAirlinePrices,
    isPerPaxPricingEnabled
}: {
    selectedLegSortedCardIds: string[]
    allCardsForSelectedLeg: FlightCard[]
    filterData: FilterData
    sectors?: Sectors
    checkedBaggageIncludedRecommendedFilter: boolean
    selectedJourneyLeg: 0 | 1
    shouldShowMultiAirlinePrices: boolean
    isPerPaxPricingEnabled: boolean
}) => {
    const filteredCards: FlightCard[] = []
    cardIds.forEach(cardId => {
        const index = Number(cardId.split(':')[1])
        const card = cards[index]
        if (
            applyFiltersToCards({
                card,
                filters: filterData,
                sectors,
                checkedBaggageIncludedRecommendedFilter,
                selectedJourneyLeg,
                shouldShowMultiAirlinePrices,
                isPerPaxPricingEnabled
            })
        ) {
            filteredCards.push(card)
        }
    })
    return filteredCards
}

/**
 * Airline card filter logic
 *
 * IMPORTANT: This filter function follows a **reject-first** approach.
 * - Each filter condition should return `false` when a card **does not satisfy** a specific criterion.
 * - The function should return `true` by default, allowing cards to pass unless explicitly rejected.
 *
 * This pattern ensures consistency and improves readability as more filters are added.
 *
 * Example:
 * if (!conditionMet) return false;
 * ...
 * return true;
 */
function applyFiltersToCards ({
    card,
    filters,
    sectors,
    checkedBaggageIncludedRecommendedFilter,
    selectedJourneyLeg,
    shouldShowMultiAirlinePrices,
    isPerPaxPricingEnabled
}: {
    card: FlightCard
    filters: FilterData
    sectors?: Sectors
    checkedBaggageIncludedRecommendedFilter: boolean
    selectedJourneyLeg: 0 | 1
    shouldShowMultiAirlinePrices: boolean
    isPerPaxPricingEnabled: boolean
}) {
    //stops filter
    const { NewStops } = filters
    const targetStops = NewStops[selectedJourneyLeg] || {}
    const { selectedValues: stopsSelectedValues } = targetStops
    const { maxStopsInSectors } = card
    if (stopsSelectedValues && stopsSelectedValues.length > 0) {
        if (!stopsSelectedValues.includes(maxStopsInSectors)) return false
    }
    //airline filter
    const { airlineFilter: airlineFilters } = filters
    const targetAirlineFilter = airlineFilters?.[selectedJourneyLeg] || {}
    const { selectedValues: airlineFilterSelectedValues } = targetAirlineFilter || {}
    const { airlineCodes } = card
    if (airlineFilterSelectedValues && airlineFilterSelectedValues?.length > 0) {
        if (!airlineCodes?.some(code => airlineFilterSelectedValues?.includes(code))) return false
    }

    //flight departure timings filter
    const { departureTimeFrom } = filters
    const targetDepartureTime = departureTimeFrom?.[selectedJourneyLeg] || {}

    const { selectedValues: departureTimeSelectedValues } = targetDepartureTime || {}

    if (departureTimeSelectedValues && departureTimeSelectedValues?.length > 0) {
        const sectorKey = card.sectorKeys[0]
        const depTimeFromCard = parseInt(sectors?.[sectorKey]?.firstDeparture.time.replace(':', '').trim() || '0')
        const timeSlot = timeToSlot(depTimeFromCard)
        if (!timeSlot || !departureTimeSelectedValues?.includes(timeSlot)) return false
    }

    //arrival flight timings filter
    const { arrivalTimeFrom } = filters
    const targetArrivalTime = arrivalTimeFrom?.[selectedJourneyLeg] || {}
    const { selectedValues: arrivalTimeSelectedValues } = targetArrivalTime || {}
    if (arrivalTimeSelectedValues && arrivalTimeSelectedValues?.length > 0) {
        const sectorKey = card.sectorKeys[0]
        const arrTimeFromCard = parseInt(sectors?.[sectorKey]?.lastArrival.time.replace(':', '').trim() || '0')
        const timeSlot = timeToSlot(arrTimeFromCard)
        if (!timeSlot || !arrivalTimeSelectedValues?.includes(timeSlot)) return false
    }
    //checked baggage included filter (recommended filter)
    if (checkedBaggageIncludedRecommendedFilter) {
        const { indicators } = card
        if (indicators?.noBaggage === true) return false
    }

    //hide budget airlines filter (recommended filter)
    // if (hideBudgetAirlinesRecommendedFilter) {
    //     const { airlineCodes } = card
    //     if (airlineCodes.some(code => code.startsWith('VY'))) return false
    // }

    //price range filter
    const { journeyPrice } = filters
    const targetJourneyPrice = journeyPrice?.[selectedJourneyLeg] || {}
    const { current: currentPriceRange } = targetJourneyPrice || {}
    const { min: minPrice, max: maxPrice } = currentPriceRange || {}
    if (!isNaN(Number(minPrice)) && !isNaN(Number(maxPrice))) {
        const {
            priceBreakup: { pr, multiAirlinePrice, totalAdultFare }
        } = card
        const price = shouldShowMultiAirlinePrices
            ? multiAirlinePrice || 0
            : isPerPaxPricingEnabled
                ? totalAdultFare || 0
                : pr
        if (price < minPrice || price > maxPrice) return false
    }

    //included airport filter (nearby airports)
    const { nbAirportFilter: nbAirportFilters } = filters
    const targetNbAirportGroups = nbAirportFilters?.[selectedJourneyLeg] || []
    const { nbAirports } = card
    const nbAirportsUpper = (nbAirports || []).map(code => code?.toUpperCase())
    const hasAnyNbSelection = targetNbAirportGroups.some(g => (g.selectedValues || []).length > 0)
    if (hasAnyNbSelection) {
        for (let groupIndex = 0; groupIndex < targetNbAirportGroups.length; groupIndex++) {
            const group = targetNbAirportGroups[groupIndex]
            const selectedValues = group.selectedValues || []
            if (!selectedValues.length) continue

            const selectedSet = new Set(selectedValues.map((v: string) => v.toUpperCase()))
            const groupAirportCode = group.airportCode?.toUpperCase()
            const groupNearbyOptions = new Set((group.options || []).map(option => option.value?.toUpperCase()))
            const selectedNearbyValues = Array.from(selectedSet).filter(value => value !== groupAirportCode)

            const cardHasNearbyForThisGroup = nbAirportsUpper.some(code => groupNearbyOptions.has(code))
            const matchesNearbyForThisGroup = selectedNearbyValues.some(value => nbAirportsUpper.includes(value))
            const matchesOriginalForThisGroup = selectedSet.has(groupAirportCode) && !cardHasNearbyForThisGroup

            if (!matchesNearbyForThisGroup && !matchesOriginalForThisGroup) return false
        }
    }


    // Add more filter conditions here
    return true
}
